import React, { createContext, useState, useContext, useCallback, ReactNode } from 'react';
import { translations } from '@/core/config/i18n';

type Locale = 'es' | 'en';
type TranslationKeys = keyof typeof translations.es;

interface I18nContextType {
    locale: Locale;
    setLocale: (locale: Locale) => void;
    t: (key: TranslationKeys, replacements?: { [key: string]: string | number }) => string;
    localeCode: 'es-ES' | 'en-US';
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const getInitialLocale = (): Locale => {
    const savedLocale = localStorage.getItem('appmorLocale');
    // Basic browser language detection as a fallback
    if (!savedLocale) {
        const browserLang = navigator.language.split('-')[0];
        if (browserLang === 'en') return 'en';
    }
    return (savedLocale === 'en' || savedLocale === 'es') ? savedLocale : 'es';
};

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

    const setLocale = (newLocale: Locale) => {
        if (translations[newLocale]) {
            localStorage.setItem('appmorLocale', newLocale);
            setLocaleState(newLocale);
        }
    };

    const t = useCallback((key: TranslationKeys, replacements?: { [key: string]: string | number }): string => {
        let translation = translations[locale][key] || translations['es'][key] || String(key);
        
        if (replacements) {
            Object.keys(replacements).forEach(placeholder => {
                translation = translation.replace(`{{${placeholder}}}`, String(replacements[placeholder]));
            });
        }

        return translation;
    }, [locale]);
    
    const localeCode = locale === 'es' ? 'es-ES' : 'en-US';

    return (
        <I18nContext.Provider value={{ locale, setLocale, t, localeCode }}>
            {children}
        </I18nContext.Provider>
    );
};

export const useI18n = () => {
    const context = useContext(I18nContext);
    if (context === undefined) {
        throw new Error('useI18n must be used within an I18nProvider');
    }
    return context;
};